import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { usePreferencePacOptions } from "./usePreferencePacOptions";
import { InputType } from "components/shared/inputs/input";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import { useMemo } from "react";
import { useDefaultFilters } from "components/EtudeForm/hooks/useDefaultFilters";
import valeurParDefaut from "utils/engine/valeurParDefaut.json";
import { getFiltresData } from "utils/engine/engine";
import { EtudeTypes } from "components/EtudeForm/constants";
import { BOILER_TYPE_GROUND, BOILER_TYPE_WALL } from "utils/helpers/boilerType";
import { useAppSelector } from "store/hooks";

export enum FilterComponentType {
  Input,
  CheckboxGroup,
}

export interface FilterItemConfig {
  component: FilterComponentType;
  name: string;
  title: string;
  type?: string;
  unit?: string;
  min?: number;
  max?: number;
  options?: { value: any; label: string; disabled?: boolean }[];
  singleOption?: boolean;
  preventUncheckAll?: boolean;
  displayCondition?: boolean;
  recalculateWhenRemoved?: boolean;
  defaultValue?: any;
  errorMessage?: string;
  required?: boolean;
}

export const objectifOptions = [
  {
    value: "re2025_ic_energie",
    label: "FO_FILTER_RE2025",
  },
  { value: "pinel_dpe_a", label: "FO_FILTER_PINEL" },
  { value: "r_cee", label: "FO_FILTER_CEE" },
];

export const boilerTypeOptions = [
  {
    value: BOILER_TYPE_GROUND,
    label: "FO_FILTER_BOILER_GROUND",
  },
  {
    value: BOILER_TYPE_WALL,
    label: "FO_FILTER_BOILER_WALL",
  },
];

export const useFilterConfig = ({
  appliedSolutionFilter,
  type,
  preference_pac,
  code_gamme_pac,
  traitement_anti_corrosion,
  hasEcs,
  hasChauffage,
  service,
  hasTcoupure,
  appointPuissanceTotale,
  currentSolutions,
}: {
  appliedSolutionFilter?: GetSolutionRequest | null;
  type?: InstallationType;
  preference_pac?: string[];
  code_gamme_pac: string[];
  nombre_de_pac?: string;
  traitement_anti_corrosion?: boolean;
  hasEcs?: boolean;
  hasChauffage?: boolean;
  service?: string;
  hasTcoupure?: boolean;
  appointPuissanceTotale?: number;
  currentSolutions?: Solution[];
}) => {
  const { network } = useSalesNetwork();
  let filtresData = getFiltresData(network?.code, service, type);
  const isWallBoilerDisabled = (appointPuissanceTotale ?? 0) > 150;
  const boilerTypeFilterOptions = boilerTypeOptions.map((option) => ({
    ...option,
    disabled: option.value === BOILER_TYPE_WALL && isWallBoilerDisabled,
  }));
  const defaultBoilerTypeFilterValues = boilerTypeFilterOptions
    .filter((option) => !option.disabled)
    .map((option) => option.value);
  const { acoustique_solution } = useAppSelector((state) => state.etude);

  const preferencePacOptions = usePreferencePacOptions({
    appliedSolutionFilter,
    type,
    code_gamme_pac,
    traitement_anti_corrosion,
    preference_pac,
    currentSolutions,
  });

  const { codeGammePacDefault, getDefaultFilters } = useDefaultFilters(
    network?.code
  );

  const defaultFilters = getDefaultFilters(type);

  const filterConfig: FilterItemConfig[] = useMemo(
    () => [
      {
        component: FilterComponentType.Input,
        title:
          hasChauffage && type === InstallationType.hybride
            ? "FO_FILTER_%_ANNUAL_COVERAGE_HYB"
            : "FO_FILTER_%_ANNUAL_COVERAGE",
        name: "taux_couverture",
        type: InputType.Number,
        unit: "FO_UNIT_PERCENT",
        min: filtresData.allData.taux_couverture?.minimum,
        max: filtresData.allData.taux_couverture?.maximum,
        displayCondition:
          service !== EtudeTypes.ECS && network?.displayResultCoverageRate,
        recalculateWhenRemoved: true,
        defaultValue: filtresData.defaults.taux_couverture,
        errorMessage: filtresData.allData.taux_couverture?.errorMessage,
        required: filtresData.defaults.taux_couverture !== "empty",
      },
      {
        component: FilterComponentType.Input,
        title:
          hasEcs && hasChauffage
            ? hasTcoupure
              ? "FO_FILTER_%_HP_@T°C_TCOUPURE_DS" : "FO_FILTER_%_HP_@T°C_BASIS_DS"
            : hasChauffage && type === InstallationType.hybride
              ? hasTcoupure
                ? "FO_FILTER_%_HP_@T°C_COUPURE_HYB"
                : "FO_FILTER_%_HP_@T°C_BASIS_HYB"
              : "FO_FILTER_%_HP_@T°C_BASIS",
        name: "part_pac",
        type: InputType.Number,
        unit: "FO_UNIT_PERCENT",
        min: filtresData.allData.part_pac?.minimum,
        max: filtresData.allData.part_pac?.maximum,
        displayCondition: network?.displayResultPACportion,
        recalculateWhenRemoved: true,
        defaultValue: filtresData.defaults.part_pac,
        errorMessage: filtresData.allData.part_pac?.errorMessage,
        required: filtresData.defaults.part_pac !== "empty",
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_EXTRAS",
        name: "appoint",
        options: [
          { value: true, label: "FO_FILTER_EXTRA_WITH" },
          { value: false, label: "FO_FILTER_EXTRA_WITHOUT" },
        ],
        displayCondition: type === InstallationType.electric,
        recalculateWhenRemoved: true,
        defaultValue: defaultFilters.appoint,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_OBJECTIVE",
        name: "objectif",
        singleOption: true,
        options: objectifOptions,
        displayCondition:
          network?.displayResultObjective && type === InstallationType.hybride,
        recalculateWhenRemoved: true,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_FLUID_TYPE",
        name: "code_gamme_pac",
        preventUncheckAll: true,
        options: filtresData.allData.code_gamme_pac?.enum,
        displayCondition: network?.displayResultFluid,
        recalculateWhenRemoved: true,
        defaultValue: filtresData.defaults.code_gamme_pac,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_HP_NUMBER",
        name: "nombre_de_pac",
        singleOption: true,
        options: [1, 2, 3].map((o) => ({
          value: o,
          label: `FO_FILTER_${o}${o > 1 ? "+" : ""}`,
        })),
        displayCondition: network?.displayResultNumberPAC,
        recalculateWhenRemoved: true,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_HP_MODEL",
        name: "preference_pac",
        options: preferencePacOptions,
        displayCondition: true,
        recalculateWhenRemoved: true,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_ACOUSTIC_COMFORT",
        name: "confort_acoustique",
        options: [
          { value: true, label: "FO_FILTER_ACOUSTIC_COMFORT_IMPROVE" },
          { value: false, label: "FO_FILTER_ACOUSTIC_COMFORT_STD" },
        ],
        singleOption: true,
        displayCondition: acoustique_solution && network?.displayResultConfortAcoustique,
        recalculateWhenRemoved: true,
      },
      {
        component: FilterComponentType.Input,
        title: "FO_FILTER_ACOUSTIC",
        name: "charge_acoustique",
        type: InputType.Number,
        unit: "FO_UNIT_DBA",
        displayCondition: network?.displayResultAcoustic,
        recalculateWhenRemoved: true,
        min: filtresData.allData.charge_acoustique?.minimum,
        max: filtresData.allData.charge_acoustique?.maximum,
        errorMessage: filtresData.allData.charge_acoustique?.errorMessage,
      },
      {
        component: FilterComponentType.Input,
        title: "FO_FILTER_ELECTRICAL_TARIF",
        name: "abonnement_elec",
        type: InputType.Number,
        unit: "FO_UNIT_KVA",
        displayCondition: network?.displayResultElectricity,
        min: filtresData.allData.abonnement_elec?.minimum,
        max: filtresData.allData.abonnement_elec?.maximum,
        recalculateWhenRemoved: true,
        errorMessage: filtresData.allData.abonnement_elec?.errorMessage,
      },
      {
        component: FilterComponentType.Input,
        title: "hauteur_sous_plafond",
        name: "hauteur_porte",
        type: InputType.Number,
        unit: "FO_UNIT_MM",
        displayCondition: network?.displayResultHeight,
        min: valeurParDefaut?.installation?.hauteur_porte?.minimum,
        max: valeurParDefaut?.installation?.hauteur_porte?.maximum,
        errorMessage: valeurParDefaut?.installation?.hauteur_porte.errorMessage,
      },
      {
        component: FilterComponentType.Input,
        title: "largeur_de_passage",
        name: "largeur_porte",
        type: InputType.Number,
        unit: "FO_UNIT_MM",
        displayCondition: network?.displayResultWidth,
        min: valeurParDefaut?.installation?.largeur_porte?.minimum,
        max: valeurParDefaut?.installation?.largeur_porte?.maximum,
        errorMessage: valeurParDefaut?.installation?.largeur_porte.errorMessage,
      },
      {
        component: FilterComponentType.CheckboxGroup,
        title: "FO_FILTER_BOILER_TYPE",
        name: "type_chaudiere",
        displayCondition: type === InstallationType.hybride && network?.displayResultBoilerType,
        preventUncheckAll: false,
        recalculateWhenRemoved: true,
        options: boilerTypeFilterOptions,
        defaultValue: defaultBoilerTypeFilterValues,
      },
    ],
    [
      network,
      type,
      hasEcs,
      hasChauffage,
      hasTcoupure,
      appointPuissanceTotale,
      preferencePacOptions,
      codeGammePacDefault,
      defaultFilters,
      boilerTypeFilterOptions,
      defaultBoilerTypeFilterValues
    ]
  );
  return filterConfig;
};
